import { useGAEventStore } from '@/store/GAEventTracking'
import { useWindowSize } from '@vueuse/core'
import type { App, WatchStopHandle } from 'vue'
import { VisibilityState } from './observe-visibility'
import { deepEqual } from '@/helpers/utils'

const thresholdForViewport = 0.5

interface GaSectionViewEl extends HTMLElement {
  _vue_gaSectionViewState?: VisibilityState
  _vue_gaSectionViewState_threshold?: number
  _vue_gaSectionViewState_fired?: boolean
  _vue_gaSectionViewState_stopWatch?: WatchStopHandle
}

export default {
  install: (app: App) => {
    const store = useGAEventStore()
    const settings = (el: GaSectionViewEl, value: any) => ({
      intersection: { threshold: el._vue_gaSectionViewState_threshold },
      throttle: 1000,
      once: true,
      callback: (isVisible: boolean) => {
        if (!isVisible) return
        // Skip if already fired
        if (el._vue_gaSectionViewState_fired) return
        el._vue_gaSectionViewState_fired = true

        if (typeof value === 'string') {
          // value can be string as shorthand value
          value = {
            sectionContent: value,
          }
        }
        store.pushSectionViewEvent(el, value)
      },
    })
    app.directive('ga-section-view', {
      mounted(el: GaSectionViewEl, { value }) {
        // Skip if already initialized (prevents re-initialization on re-render)
        if (el._vue_gaSectionViewState) return

        const { height: winHeight } = useWindowSize()
        const { height } = useElementSize(el)
        el._vue_gaSectionViewState_threshold = thresholdForViewport

        el._vue_gaSectionViewState_stopWatch = watchDebounced(
          [height, winHeight],
          () => {
            if (height.value > winHeight.value) {
              const newThreshold = (winHeight.value * thresholdForViewport) / height.value
              el._vue_gaSectionViewState_threshold = newThreshold
              const state = el._vue_gaSectionViewState
              state?.createObserver(settings(el, value))
            }
          },
          { debounce: 100, maxWait: 400 }
        )

        el._vue_gaSectionViewState = new VisibilityState(el, settings(el, value))
      },
      updated(el: GaSectionViewEl, { value, oldValue }) {
        if (deepEqual(value, oldValue)) return
        const state = el._vue_gaSectionViewState
        if (state) {
          state.createObserver(settings(el, value))
        }
      },
      unmounted(el: GaSectionViewEl) {
        const state = el._vue_gaSectionViewState

        if (state) {
          state.destroyObserver()
          delete el._vue_gaSectionViewState
        }

        // Stop the watcher
        el._vue_gaSectionViewState_stopWatch?.()
        delete el._vue_gaSectionViewState_stopWatch
        delete el._vue_gaSectionViewState_threshold
        delete el._vue_gaSectionViewState_fired
      },
    })
  },
}
